import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import ScheduledMessagesAPI from '../../api/scheduledMessages';
import { throwErrorMessage } from '../utils/api';

const state = {
  records: [],
  meta: {
    count: 0,
    currentPage: 1,
    perPage: 15,
    statusCounts: {
      all: 0,
      pending: 0,
      processing: 0,
      sent: 0,
      failed: 0,
      cancelled: 0,
    },
    statistics: {
      pendingToday: 0,
      thisWeek: 0,
      sentTotal: 0,
      cancelledTotal: 0,
    },
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getScheduledMessages: $state => $state.records,
  getMeta: $state => $state.meta,
  getUIFlags: $state => $state.uiFlags,
  getByConversation: $state => conversationId =>
    $state.records.filter(r => r.conversation_id === conversationId),
  getById: $state => id => $state.records.find(r => r.id === id),
  getPending: $state => $state.records.filter(r => r.status === 'pending'),
  getActive: $state =>
    $state.records.filter(r => ['pending', 'processing'].includes(r.status)),
};

export const actions = {
  get: async ({ commit }, { page = 1, ...filters } = {}) => {
    commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: true });
    try {
      const response = await ScheduledMessagesAPI.get({ page, ...filters });
      const { payload, meta } = response.data;
      commit(types.default.SET_SCHEDULED_MESSAGES, payload);
      commit(types.default.SET_SCHEDULED_MESSAGES_META, {
        count: meta.count,
        currentPage: meta.current_page,
        perPage: meta.per_page,
        statusCounts: meta.status_counts || {},
        statistics: {
          pendingToday: meta.statistics?.pending_today || 0,
          thisWeek: meta.statistics?.this_week || 0,
          sentTotal: meta.statistics?.sent_total || 0,
          cancelledTotal: meta.statistics?.cancelled_total || 0,
        },
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, {
        isFetching: false,
      });
    }
  },

  show: async ({ commit }, id) => {
    commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: true });
    try {
      const response = await ScheduledMessagesAPI.show(id);
      commit(types.default.ADD_SCHEDULED_MESSAGE, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, {
        isFetching: false,
      });
    }
    return null;
  },

  create: async ({ commit }, data) => {
    commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, { isCreating: true });
    try {
      const response = await ScheduledMessagesAPI.create(data);
      commit(types.default.ADD_SCHEDULED_MESSAGE, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, {
        isCreating: false,
      });
    }
    return null;
  },

  update: async ({ commit }, { id, ...data }) => {
    commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, { isUpdating: true });
    try {
      const response = await ScheduledMessagesAPI.update(id, data);
      commit(types.default.UPDATE_SCHEDULED_MESSAGE, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, {
        isUpdating: false,
      });
    }
    return null;
  },

  delete: async ({ commit }, id) => {
    commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, { isDeleting: true });
    try {
      await ScheduledMessagesAPI.delete(id);
      commit(types.default.DELETE_SCHEDULED_MESSAGE, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, {
        isDeleting: false,
      });
    }
  },

  cancel: async ({ commit }, id) => {
    commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, { isUpdating: true });
    try {
      const response = await ScheduledMessagesAPI.cancel(id);
      commit(types.default.UPDATE_SCHEDULED_MESSAGE, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, {
        isUpdating: false,
      });
    }
    return null;
  },

  reactivate: async ({ commit }, id) => {
    commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, { isUpdating: true });
    try {
      const response = await ScheduledMessagesAPI.reactivate(id);
      commit(types.default.UPDATE_SCHEDULED_MESSAGE, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, {
        isUpdating: false,
      });
    }
    return null;
  },

  sendNow: async ({ commit }, id) => {
    commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, { isUpdating: true });
    try {
      await ScheduledMessagesAPI.sendNow(id);
      commit(types.default.DELETE_SCHEDULED_MESSAGE, id);
      return true;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, {
        isUpdating: false,
      });
    }
    return false;
  },

  bulkCancel: async ({ commit }, ids) => {
    commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, { isUpdating: true });
    try {
      await ScheduledMessagesAPI.bulkCancel(ids);
      // Update local state to cancelled
      ids.forEach(id => {
        commit(types.default.UPDATE_SCHEDULED_MESSAGE, {
          id,
          status: 'cancelled',
        });
      });
      return true;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, {
        isUpdating: false,
      });
    }
    return false;
  },

  bulkDestroy: async ({ commit }, ids) => {
    commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, { isDeleting: true });
    try {
      await ScheduledMessagesAPI.bulkDestroy(ids);
      ids.forEach(id => {
        commit(types.default.DELETE_SCHEDULED_MESSAGE, id);
      });
      return true;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SCHEDULED_MESSAGES_UI_FLAG, {
        isDeleting: false,
      });
    }
    return false;
  },
};

export const mutations = {
  [types.default.SET_SCHEDULED_MESSAGES_UI_FLAG]: ($state, data) => {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.default.SET_SCHEDULED_MESSAGES]: MutationHelpers.set,
  [types.default.SET_SCHEDULED_MESSAGES_META]: ($state, data) => {
    $state.meta = { ...$state.meta, ...data };
  },
  [types.default.ADD_SCHEDULED_MESSAGE]: MutationHelpers.create,
  [types.default.UPDATE_SCHEDULED_MESSAGE]: MutationHelpers.update,
  [types.default.DELETE_SCHEDULED_MESSAGE]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
